<!-- 颜色饱和度、明度选择器，只需要传入rgb即可 -->
<template>
  <div
    ref="pickerRef"
    class="xl:h-full xl:aspect-square w-60 h-60 shrink-0 rounded-3xl bg-linear-to-br shadow-inner overflow-hidden cursor-move relative bg-[linear-gradient(to_top,#000,transparent),linear-gradient(to_right,#fff,transparent)]"
    :style="{
      backgroundColor: baseHueColor,
    }"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <div
      class="absolute w-3 h-3 rounded-full border-2 glass-frosted pointer-events-none z-10 border-white"
      :style="{
        zIndex: 10,
        left: `${saturation * 100}%`,
        top: `${(1 - value) * 100}%`,
        transform: 'translate(-50%, -50%)',
      }"
    ></div>
  </div>
</template>

<script setup lang="ts">
  import type { RGB } from '@rapoo/device-sdk'

  import { ref, onUnmounted, watch, computed } from 'vue'
  import { rgbToHsv, rgbToHex, hsvToRgb } from '@/utils/color'
  import { throttle } from '@/utils/optimize'

  const props = withDefaults(
    defineProps<{
      rgb?: RGB
    }>(),
    {
      rgb: () => ({ r: 0, g: 0, b: 0 }),
    },
  )

  const emit = defineEmits(['update:rgb', 'change'])

  // 锁定的 hue 的值
  const lockedHue = ref(rgbToHsv(props.rgb).h)
  // 可修改的饱和度/明度
  const saturation = ref(rgbToHsv(props.rgb).s)
  const value = ref(rgbToHsv(props.rgb).v)
  // 是否正在拖拽（拖拽时需要强制锁死 hue，否则会导致跳跃）
  const isDragging = ref(false)

  // 基色：始终基于 lockedHue 生成
  const baseHueColor = computed(() => {
    const pureHsv = { h: lockedHue.value, s: 1, v: 1 }
    return rgbToHex(hsvToRgb(pureHsv))
  })

  // ========== 监听外部 RGB 变化（仅非拖拽时更新 lockedHue） ==========
  watch(
    () => props.rgb,
    (newRgb) => {
      const newHsv = rgbToHsv(newRgb)
      // 注意：只有非拖拽状态，才更新 lockedHue（外部改 RGB 时同步基色）
      if (!isDragging.value) {
        lockedHue.value = newHsv.h
        saturation.value = newHsv.s
        value.value = newHsv.v
      }
    },
    { deep: true, immediate: true },
  )

  // ========== 拖拽逻辑（全程锁死 lockedHue） ==========
  const pickerRef = ref<HTMLDivElement>()

  // 开始拖拽
  const startDrag = (e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    isDragging.value = true // 标记进入拖拽状态，禁止 hue 更新
    rectCache.value = pickerRef.value?.getBoundingClientRect()
    updatePosition(e)
    // 绑定全局事件
    document.addEventListener('mousemove', handleDrag)
    document.addEventListener('mouseup', stopDrag)
    document.addEventListener('touchmove', handleDrag, { passive: false })
    document.addEventListener('touchend', stopDrag)
  }

  // 拖拽中
  const handleDrag = throttle((e: MouseEvent | TouchEvent) => {
    if (!isDragging.value || !pickerRef.value) return
    // e.preventDefault()
    updatePosition(e)
  }, 16)

  // 结束拖拽
  const stopDrag = () => {
    cleanup(false)
  }

  const cleanup = (isUnmounted: boolean = false) => {
    isDragging.value = false
    if (!isUnmounted) {
      emit('change', props.rgb)
    }
    document.removeEventListener('mousemove', handleDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.removeEventListener('touchmove', handleDrag)
    document.removeEventListener('touchend', stopDrag)
  }

  // 更新位置 + 生成新 RGB
  const rectCache = ref<DOMRect>()
  const updatePosition = (e: MouseEvent | TouchEvent) => {
    if (!pickerRef.value) return

    if (!rectCache.value) {
      rectCache.value = pickerRef.value.getBoundingClientRect()
    }
    const width = rectCache.value.width
    const height = rectCache.value.height

    // 兼容坐标
    let clientX = 0,
      clientY = 0
    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    // 仅计算 s/v，hue 完全锁死
    const newSaturation = Math.max(0, Math.min(1, (clientX - rectCache.value.left) / width))
    const newValue = Math.max(0, Math.min(1, 1 - (clientY - rectCache.value.top) / height))

    saturation.value = newSaturation
    value.value = newValue

    // 强制使用 lockedHue，避免 hue 偏移
    const newHsv = {
      h: lockedHue.value, // 全程锁死
      s: newSaturation,
      v: newValue,
    }
    const newRgb = hsvToRgb(newHsv)
    emit('update:rgb', newRgb)
  }

  // 清理事件
  onUnmounted(() => {
    cleanup(true)
  })
</script>
